import { useEffect } from 'react'

import { useGetVersionQuery } from '@sketch/gql-types'
import { getVersionRenderStatus } from '@sketch/modules-common'

const POLLING_INTERVAL = 15000

/**
 * Wrapper around useGetVersionQuery to refetch document version
 * every X seconds in case the document is still processing.
 * We need to do this refetch because as a way to be notified when the document
 * version render status changes ("uploading", "processing", "ready").
 * Once the document is "ready" we can stop polling.
 */
export function useGetVersionQueryWithPolling(
  shareID: string,
  versionShortId: string
) {
  const queryResult = useGetVersionQuery({
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.versionShortId !== curr?.versionShortId,
    variables: {
      shareIdentifier: shareID,
      versionShortId,
    },
  })

  const { data, error, startPolling, stopPolling } = queryResult
  const renderStatus = getVersionRenderStatus(data?.share?.version)

  useEffect(() => {
    const isWaitingForDocumentToBeProcessed =
      renderStatus === 'uploading' || renderStatus === 'processing'

    if (!isWaitingForDocumentToBeProcessed) {
      return
    }

    startPolling(POLLING_INTERVAL)

    return () => {
      stopPolling()
    }
  }, [renderStatus, startPolling, stopPolling, error])

  return queryResult
}
